/**
 * The mid-session "you've been signed out" prompt for the logout-on-401
 * mutation path (design §1.3, §6.1).
 *
 * A mutation 401 means the user was *acting* (e.g. clicking Save on a
 * half-filled form), so we must not redirect out from under them and
 * discard their input. Instead we raise a non-blocking,
 * non-auto-dismissing toast with a **Sign in** action; the form stays
 * mounted behind it so the user can copy anything they need before
 * re-authenticating on their own terms.
 *
 * Split from ``lib/auth/session-expiry.ts`` because building the action
 * needs a React element; the pure routing decision stays framework-
 * agnostic over there.
 *
 * ``duration: Infinity`` disables Radix's auto-close timer (the toaster
 * already uses a long remove delay and a limit of 1, so the prompt
 * persists and is never crowded out). ``onSignIn`` defaults to a hard
 * ``window.location.assign`` to the sign-in target; it is injectable so
 * tests can assert the wiring without a real navigation — jsdom's
 * ``window.location`` is immutable and cannot be stubbed.
 */

"use client"

import { ToastAction } from "@/components/ui/toast"
import { toast } from "@/hooks/use-toast"

export function showSessionExpiredToast(
  target: string,
  onSignIn?: () => void,
): void {
  const navigate = onSignIn ?? (() => window.location.assign(target))
  toast({
    duration: Infinity,
    title: "You've been signed out",
    description: "Sign in again to continue — your changes are still here.",
    action: (
      <ToastAction altText="Sign in" onClick={navigate}>
        Sign in
      </ToastAction>
    ),
  })
}
